import { useState, useCallback, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet, Navigate } from "react-router-dom";
import { resolveAvatarSrc } from "@/pages/ProfilePage";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useTheme } from "@/context/ThemeContext";
import axios from "axios";
import {
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  Users,
  Trash2,
  Loader2,
  User,
  ChevronUp,
  Moon,
  Sun,
  MonitorSmartphone,
  Palette,
  LogOut as LogOutIcon,
  BarChart3,
  Bell,
  FileWarning,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const BRAND_COLORS = [
  { name: "Philadelphia", value: "#FBCC49" },
  { name: "Clementine", value: "#7EC9C9" },
  { name: "2049", value: "#6763A9" },
  { name: "Wade", value: "#E44E50" },
  { name: "Budapest", value: "#DE9DAF" },
  { name: "Inception Black", value: "#262626" },
];

function ThemeModePicker({ t }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { dark, mode, setMode } = useTheme();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const options = [
    { key: "light", label: t("light_mode") || "Light", icon: Sun },
    { key: "dark", label: t("dark_mode") || "Dark", icon: Moon },
    { key: "auto", label: t("auto_mode") || "Automatic", icon: MonitorSmartphone },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="theme-mode-toggle"
        onClick={() => setOpen(!open)}
        className="p-2 rounded-lg text-[#6F6C65] dark:text-[var(--dm-muted-text)] hover:text-[#1B1D21] dark:hover:text-[var(--dm-text)] hover:bg-[#EFEDE8] dark:hover:bg-[var(--dm-hover)] transition-colors"
        title={t("theme_mode") || "Theme Mode"}
      >
        {mode === "auto" ? <MonitorSmartphone className="w-4 h-4" /> : dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 start-0 bg-white dark:bg-[var(--dm-card)] ring-1 ring-[#E4E1D9] dark:ring-[var(--dm-input-border)] rounded-xl shadow-xl z-50 p-1.5 min-w-[150px]">
          {options.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              data-testid={`theme-mode-${key}`}
              onClick={() => { setMode(key); setOpen(false); }}
              className={`flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-[13px] transition-colors ${
                mode === key ? "bg-[#1B1D21] text-white dark:bg-[var(--dm-btn-primary-bg)] dark:text-[var(--dm-btn-primary-text)]" : "text-[#3A3B3F] dark:text-[var(--dm-text-secondary)] hover:bg-[#F6F5F2] dark:hover:bg-[var(--dm-hover)]"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ThemeColorPicker({ user }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { updateTheme } = useAuth();
  const themeColor = user?.theme_color || "#FBCC49";

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const pick = (color) => {
    updateTheme(color);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="sidebar-theme-color-btn"
        onClick={() => setOpen(!open)}
        className="p-2 rounded-lg text-[#6F6C65] dark:text-[var(--dm-muted-text)] hover:text-[#1B1D21] dark:hover:text-[var(--dm-text)] hover:bg-[#EFEDE8] dark:hover:bg-[var(--dm-hover)] transition-colors"
        title="Theme"
      >
        <Palette className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 start-0 bg-white dark:bg-[var(--dm-card)] ring-1 ring-[#E4E1D9] dark:ring-[var(--dm-input-border)] rounded-xl shadow-xl z-50 p-3 min-w-[180px]">
          <p className="text-[10px] font-semibold text-[#6F6C65] dark:text-[var(--dm-placeholder)] uppercase tracking-wider mb-2">Theme</p>
          <div className="grid grid-cols-6 gap-2">
            {BRAND_COLORS.map((c) => (
              <button
                key={c.value}
                data-testid={`sidebar-color-${c.name.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => pick(c.value)}
                className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
                  themeColor === c.value ? "border-[#1B1D21] dark:border-white scale-110 ring-2 ring-[#1B1D21]/20 dark:ring-white/20" : "border-[#E4E1D9] dark:border-white/10"
                }`}
                style={{ backgroundColor: c.value }}
                title={c.name}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PdfAlertsBell({ user, authHeader, t }) {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const ref = useRef(null);
  const navigate = useNavigate();
  const canSee = user?.role === "admin" || user?.role === "finance_manager";

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!canSee) return;
    const fetchAlerts = () => {
      axios.get(`${API}/quotations/pending-pdf-alerts`, { headers: authHeader() }).then((r) => setAlerts(r.data || [])).catch(() => {});
    };
    fetchAlerts();
    const iv = setInterval(fetchAlerts, 60000);
    return () => clearInterval(iv);
  }, [canSee]); // eslint-disable-line

  if (!canSee) return null;

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="pdf-alerts-bell"
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg text-[#6F6C65] dark:text-[var(--dm-muted-text)] hover:text-[#1B1D21] dark:hover:text-[var(--dm-text)] hover:bg-[#EFEDE8] dark:hover:bg-[var(--dm-hover)] transition-colors"
        title={t("pdf_alerts_title") || "PDF Missing"}
      >
        <FileWarning className="w-4 h-4" />
        {alerts.length > 0 && (
          <span data-testid="pdf-alerts-count" className="absolute -top-0.5 -end-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#F59E0B] text-white text-[9px] font-bold flex items-center justify-center">
            {alerts.length}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 start-0 bg-white dark:bg-[var(--dm-card)] ring-1 ring-[#E4E1D9] dark:ring-[var(--dm-input-border)] rounded-xl shadow-xl z-50 p-2 min-w-[220px] max-h-64 overflow-y-auto">
          <p className="text-[11px] font-medium text-[#6F6C65] dark:text-[var(--dm-muted-text)] px-1 pb-1">{t("pdf_alerts_title") || "PDF Missing"}</p>
          {alerts.length > 0 ? alerts.map((a) => (
            <button key={a.id} data-testid={`pdf-alert-${a.id}`}
              onClick={() => { setOpen(false); navigate(`/quotations/${a.id}`); }}
              className="w-full text-start px-2 py-2 rounded-lg hover:bg-[#F59E0B]/10 transition-colors">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-semibold text-[#1B1D21] dark:text-[var(--dm-text)]">{a.quote_number}</p>
                <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-[#F59E0B]/15 text-[#F59E0B]">
                  {a.alert_type === "invoice" ? (t("invoice_pdf") || "Invoice") : (t("quotation_pdf") || "Quotation")}
                </span>
              </div>
              <p className="text-[11px] text-[#6F6C65] dark:text-[var(--dm-placeholder)] truncate" dir="auto">{a.client_name}</p>
            </button>
          )) : (
            <p className="text-[11px] text-[#6F6C65] dark:text-[var(--dm-placeholder)] px-1 py-1">{t("no_pdf_alerts") || "All caught up"}</p>
          )}
        </div>
      )}
    </div>
  );
}

function timeAgo(iso, t) {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t("time_ago_now") || "now";
  if (mins < 60) return (t("time_ago_minutes") || "{n}m").replace("{n}", mins);
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return (t("time_ago_hours") || "{n}h").replace("{n}", hrs);
  return (t("time_ago_days") || "{n}d").replace("{n}", Math.floor(hrs / 24));
}

function NotificationsBell({ authHeader, t }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    const fetchNotifications = () => {
      axios.get(`${API}/notifications`, { headers: authHeader() })
        .then((r) => { setItems(r.data?.notifications || []); setUnreadCount(r.data?.unread_count || 0); })
        .catch(() => {});
    };
    fetchNotifications();
    const iv = setInterval(fetchNotifications, 30000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      axios.get(`${API}/notifications`, { headers: authHeader() })
        .then((r) => { setItems(r.data?.notifications || []); setUnreadCount(r.data?.unread_count || 0); })
        .catch(() => {});
    }
  };

  const handleOpenNotification = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await axios.put(`${API}/notifications/${n.id}/read`, {}, { headers: authHeader() }); } catch {}
      setItems((prev) => prev.map((it) => (it.id === n.id ? { ...it, read: true } : it)));
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    navigate(`/quotations/${n.quotation_id}`);
  };

  const handleMarkAllRead = async (e) => {
    e.stopPropagation();
    try { await axios.put(`${API}/notifications/mark-all-read`, {}, { headers: authHeader() }); } catch {}
    setItems((prev) => prev.map((it) => ({ ...it, read: true })));
    setUnreadCount(0);
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notifications-bell"
        onClick={toggleOpen}
        className="relative p-2 rounded-lg text-[#6F6C65] dark:text-[var(--dm-muted-text)] hover:text-[#1B1D21] dark:hover:text-[var(--dm-text)] hover:bg-[#EFEDE8] dark:hover:bg-[var(--dm-hover)] transition-colors"
        title={t("notifications_title") || "Notifications"}
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span data-testid="notifications-count" className="absolute -top-0.5 -end-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#E44E50] text-white text-[9px] font-bold flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 start-0 bg-white dark:bg-[var(--dm-card)] ring-1 ring-[#E4E1D9] dark:ring-[var(--dm-input-border)] rounded-xl shadow-xl z-50 p-2 min-w-[260px] max-h-80 overflow-y-auto">
          <div className="flex items-center justify-between px-1 pb-1">
            <p className="text-[11px] font-medium text-[#6F6C65] dark:text-[var(--dm-muted-text)]">{t("notifications_title") || "Notifications"}</p>
            {unreadCount > 0 && (
              <button data-testid="mark-all-notifications-read" onClick={handleMarkAllRead}
                className="text-[10px] font-semibold text-[#3B82F6] hover:underline">
                {t("mark_all_read") || "Mark all read"}
              </button>
            )}
          </div>
          {items.length > 0 ? items.map((n) => (
            <button key={n.id} data-testid={`notification-item-${n.id}`}
              onClick={() => handleOpenNotification(n)}
              className={`w-full text-start px-2 py-2 rounded-lg transition-colors ${n.read ? "hover:bg-[#EFEDE8] dark:hover:bg-[var(--dm-hover)]" : "bg-[#3B82F6]/5 hover:bg-[#3B82F6]/10"}`}>
              <div className="flex items-start gap-1.5">
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] mt-1 flex-shrink-0" />}
                <div className="min-w-0">
                  <p className={`text-xs ${n.read ? "font-medium text-[#1B1D21] dark:text-[var(--dm-text)]" : "font-semibold text-[#1B1D21] dark:text-[var(--dm-text)]"}`}>{t(`notif_title_${n.type}`) !== `notif_title_${n.type}` ? t(`notif_title_${n.type}`) : n.title}</p>
                  <p className="text-[11px] text-[#6F6C65] dark:text-[var(--dm-placeholder)] truncate" dir="auto">{n.message}</p>
                  <p className="text-[10px] text-[#6F6C65]/60 dark:text-[var(--dm-placeholder)]/60 mt-0.5">{timeAgo(n.created_at, t)}</p>
                </div>
              </div>
            </button>
          )) : (
            <p className="text-[11px] text-[#6F6C65] dark:text-[var(--dm-placeholder)] px-1 py-1">{t("no_notifications") || "No notifications yet"}</p>
          )}
        </div>
      )}
    </div>
  );
}

function ProfileMenu({ user, t, onLogout, onNav, mobile }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const avatarUrl = resolveAvatarSrc(user?.profile_picture);

  return (
    <div ref={menuRef} className="relative">
      {/* Dropdown — opens upward */}
      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-1.5 bg-white dark:bg-[var(--dm-card)] rounded-xl ring-1 ring-[#E4E1D9] dark:ring-[var(--dm-input-border)] shadow-xl overflow-hidden z-50">
          <Link
            to="/profile"
            data-testid="menu-profile"
            onClick={() => { setOpen(false); onNav(); }}
            className="flex items-center gap-2.5 px-3 py-2.5 text-[13px] text-[#3A3B3F] dark:text-[var(--dm-text-secondary)] hover:bg-[#F6F5F2] dark:hover:bg-[var(--dm-hover)] hover:text-[#1B1D21] dark:hover:text-[var(--dm-text)] transition-colors"
          >
            <User className="w-3.5 h-3.5" />
            {t("profile_title")}
          </Link>
          <button
            data-testid="logout-btn"
            onClick={() => { setOpen(false); onLogout(); }}
            className="flex items-center gap-2.5 w-full px-3 py-2.5 text-[13px] text-[#B3261E]/80 hover:bg-[#B3261E]/5 hover:text-[#B3261E] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            {t("sign_out")}
          </button>
        </div>
      )}

      {/* Trigger — white user card */}
      <button
        data-testid="profile-menu-trigger"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2.5 w-full px-3 py-2.5 rounded-2xl bg-white dark:bg-[var(--dm-card)] shadow-[0_1px_2px_rgba(27,29,33,0.04),0_4px_12px_rgba(27,29,33,0.05),0_14px_32px_rgba(27,29,33,0.06)] dark:shadow-[var(--dm-card-shadow)] hover:brightness-[0.99] transition-all"
      >
        <div className="w-8 h-8 rounded-full bg-[#F3CF5B] flex items-center justify-center text-[#1B1D21] font-bold text-[12px] flex-shrink-0 overflow-hidden">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            user?.name?.[0]?.toUpperCase() || "U"
          )}
        </div>
        <div className="flex-1 min-w-0 text-start">
          <p className="text-[13px] font-semibold text-[#1B1D21] dark:text-[var(--dm-text-secondary)] truncate">{user?.name}</p>
          <p className="text-[10px] text-[#6F6C65] dark:text-[var(--dm-placeholder)] font-mono">v{process.env.REACT_APP_VERSION}</p>
        </div>
        <ChevronUp className={`w-3.5 h-3.5 text-[#6F6C65] dark:text-[var(--dm-placeholder)] transition-transform flex-shrink-0 ${open ? "" : "rotate-180"}`} />
      </button>
    </div>
  );
}

function NavGroup({ title, items, isActive, handleNav }) {
  if (!items.length) return null;
  return (
    <div className="mb-4">
      <p className="text-xs font-medium text-[#6F6C65] dark:text-[var(--dm-placeholder)] mb-1.5 px-3">{title}</p>
      <div className="space-y-0.5">
        {items.map((item) => {
          const active = isActive(item.href);
          return (
            <Link
              key={item.href}
              to={item.href}
              data-testid={`nav-${item.label.replace(/\s/g, "-").toLowerCase()}`}
              onClick={handleNav}
              className={`flex items-center gap-3 px-3 h-10 rounded-xl text-sm font-medium transition-all ${
                active
                  ? "bg-[#F3CF5B] text-[#1B1D21]"
                  : "text-[#3A3B3F] dark:text-[var(--dm-muted-text)] hover:bg-[#EFEDE8] dark:hover:bg-[var(--dm-hover)] hover:text-[#1B1D21] dark:hover:text-[var(--dm-text-secondary)]"
              }`}
            >
              <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
              <span className="flex-1">{item.label}</span>
              {item.badge != null && (
                <span
                  className={`text-[11px] font-mono font-semibold px-1.5 py-0.5 rounded-md ${
                    active ? "bg-[#1B1D21] text-white dark:text-[var(--dm-text)]" : "bg-[#EFEDE8] dark:bg-[var(--dm-chip-bg)] text-[#6F6C65] dark:text-[var(--dm-muted-text)]"
                  }`}
                >
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function Sidebar({ mobile = false, onClose }) {
  const { user, logout, authHeader } = useAuth();
  const { t, lang, toggleLang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [rfqCount, setRfqCount] = useState(null);
  const [showOnlineList, setShowOnlineList] = useState(false);
  const onlineRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (onlineRef.current && !onlineRef.current.contains(e.target)) setShowOnlineList(false); };
    if (showOnlineList) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showOnlineList]);

  // Heartbeat + fetch online users
  useEffect(() => {
    if (!user) return;
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    const canViewOnline = (user?.permissions || []).includes("view_online_users");
    const ping = () => {
      axios.post(`${API}/users/heartbeat`, {}, { headers }).catch(() => {});
      if (canViewOnline) {
        axios.get(`${API}/users/online`, { headers }).then((r) => setOnlineUsers(r.data || [])).catch(() => {});
      }
    };
    ping();
    const iv = setInterval(ping, 30000);
    return () => clearInterval(iv);
  }, [user]);

  // RFQ count badge
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/quotations`, { headers: authHeader() }).then((r) => setRfqCount((r.data || []).length)).catch(() => {});
  }, [user]); // eslint-disable-line

  const canManageSettings = (user?.permissions || []).includes("manage_settings");
  const canViewOnline = (user?.permissions || []).includes("view_online_users");
  const hasClientsAccess = canManageSettings || user?.role === "finance_manager";

  const workspaceItems = [
    { label: t("quotations"), icon: FileText, href: "/dashboard", badge: rfqCount },
    { label: t("trash"), icon: Trash2, href: "/trash" },
    ...(hasClientsAccess ? [{ label: t("clients"), icon: Users, href: "/clients" }] : []),
  ];
  const insightsItems = canManageSettings ? [{ label: t("analytics") || "Analytics", icon: BarChart3, href: "/analytics" }] : [];
  const systemItems = canManageSettings ? [{ label: t("admin"), icon: Settings, href: "/admin" }] : [];

  const isActive = (href) => {
    if (href === "/dashboard") return location.pathname === "/dashboard" || location.pathname.startsWith("/quotations");
    return location.pathname.startsWith(href);
  };

  const handleLogout = () => { logout(); navigate("/login"); };
  const handleNav = () => { if (mobile) onClose?.(); };

  const sortedOnline = [...onlineUsers].sort((a, b) => (a.id === user?.id ? -1 : b.id === user?.id ? 1 : 0));

  return (
    <div
      className={`flex flex-col h-full ${mobile ? "w-64" : "w-[256px]"} ${
        mobile ? "bg-white dark:bg-[var(--dm-page)]" : "bg-transparent dark:bg-[var(--dm-page)]"
      } text-[#1B1D21] dark:text-[var(--dm-text)]`}
    >
      {/* Logo */}
      <div className="flex items-center justify-between px-3 py-4">
        <div className="flex items-center gap-2.5">
          <img src="/logo.png" alt="RFQ Hub" className="h-5 w-auto rounded object-contain dark:brightness-0 dark:invert opacity-90" />
          <span className="text-[11px] text-[#6F6C65] dark:text-[var(--dm-placeholder)] font-medium tracking-wide">منصة رفق</span>
        </div>
        {mobile && (
          <button onClick={onClose} className="text-[#6F6C65] dark:text-[var(--dm-muted-text)] hover:text-[#1B1D21] dark:hover:text-[var(--dm-text)] transition-colors">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* New RFQ */}
      <div className="px-3 pb-4">
        <Link
          to="/quotations/new"
          data-testid="nav-new-quotation"
          onClick={handleNav}
          className="flex items-center justify-center gap-2 w-full h-[46px] rounded-2xl text-[13px] font-semibold bg-[#1B1D21] dark:bg-[var(--dm-btn-primary-bg)] text-white dark:text-[var(--dm-btn-primary-text)] hover:bg-[#2A2D32] dark:hover:brightness-95 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" style={{ color: "var(--icon-primary-btn)" }} strokeWidth={2.5} />
          {t("new_quotation")}
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-1 overflow-y-auto">
        <NavGroup title={t("workspace")} items={workspaceItems} isActive={isActive} handleNav={handleNav} />
        <NavGroup title={t("insights")} items={insightsItems} isActive={isActive} handleNav={handleNav} />
        <NavGroup title={t("system")} items={systemItems} isActive={isActive} handleNav={handleNav} />
      </nav>

      {/* Online indicator — click "Active" to reveal who's online */}
      {canViewOnline && (
      <div ref={onlineRef} className="px-4 py-2 flex-shrink-0 relative">
        <button
          data-testid="active-users-toggle"
          onClick={() => setShowOnlineList(!showOnlineList)}
          className="flex items-center gap-1.5 w-full px-2.5 py-1.5 rounded-lg border border-[#E4E1D9] dark:border-[var(--dm-input-border)] hover:bg-[#EFEDE8] dark:hover:bg-[var(--dm-hover)] transition-colors"
        >
          <span className="w-2 h-2 rounded-full bg-[#1F7A45] flex-shrink-0" />
          <span className="text-[11px] font-medium text-[#6F6C65] dark:text-[var(--dm-muted-text)]">{t("active_members") || "Active Members"} ({onlineUsers.length})</span>
          <ChevronUp className={`w-3 h-3 text-[#6F6C65] dark:text-[var(--dm-placeholder)] transition-transform ms-auto ${showOnlineList ? "" : "rotate-180"}`} />
        </button>
        {showOnlineList && (
          <div className="absolute bottom-full start-4 end-4 mb-1.5 bg-white dark:bg-[var(--dm-card)] ring-1 ring-[#E4E1D9] dark:ring-[var(--dm-input-border)] rounded-xl shadow-xl z-50 p-2 max-h-64 overflow-y-auto">
            <p className="text-[11px] font-medium text-[#6F6C65] dark:text-[var(--dm-muted-text)] px-1 pb-1">{t("online") || "Online"}</p>
            {sortedOnline.length > 0 ? sortedOnline.map((u) => {
              const isSelf = u.id === user?.id;
              return (
              <div key={u.id} className="flex items-center gap-2.5 px-1 py-1 group/online">
                <div className="relative flex-shrink-0">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                    style={{ backgroundColor: u.badge_color ? `${u.badge_color}25` : "rgba(27,29,33,0.08)", color: u.badge_color || "#1B1D21" }}>
                    {u.name?.[0]?.toUpperCase() || "?"}
                  </div>
                  <span className="absolute -bottom-0.5 -end-0.5 w-2.5 h-2.5 bg-[#1F7A45] rounded-full ring-2 ring-white dark:ring-[var(--dm-card)]" />
                </div>
                <span className="text-[12px] text-[#3A3B3F] dark:text-[var(--dm-muted-text)] truncate flex-1">
                  {u.name}{isSelf && <span className="text-[#6F6C65]/60 dark:text-[var(--dm-placeholder)] font-normal"> ({t("you") || "You"})</span>}
                </span>
                {!isSelf && (
                  <button
                    data-testid={`kick-user-${u.id}`}
                    onClick={async () => {
                      if (!window.confirm(t("kick_confirm") || "Force logout this user?")) return;
                      try {
                        const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
                        await axios.post(`${API}/users/${u.id}/kick`, {}, { headers });
                        toast.success(t("user_kicked") || "User kicked");
                        setOnlineUsers((prev) => prev.filter((ou) => ou.id !== u.id));
                      } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                    }}
                    className="p-1 rounded text-transparent group-hover/online:text-[#6F6C65] hover:!text-[#B3261E] transition-colors flex-shrink-0"
                    title={t("kick_user") || "Kick"}
                  >
                    <LogOutIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
              );
            }) : (
              <p className="text-[11px] text-[#6F6C65] dark:text-[var(--dm-placeholder)] px-1 py-1">{t("no_other_users_online") || "No one else online"}</p>
            )}
          </div>
        )}
      </div>
      )}

      {/* Theme & Dark mode icons */}
      <div className="px-4 py-2 flex-shrink-0 flex items-center gap-2">
        <NotificationsBell authHeader={authHeader} t={t} />
        <PdfAlertsBell user={user} authHeader={authHeader} t={t} />
        <ThemeModePicker t={t} />
        <ThemeColorPicker user={user} />
        <button
          data-testid="language-toggle"
          onClick={toggleLang}
          className="p-2 rounded-lg text-[11px] font-semibold text-[#6F6C65] dark:text-[var(--dm-muted-text)] hover:text-[#1B1D21] dark:hover:text-[var(--dm-text)] hover:bg-[#EFEDE8] dark:hover:bg-[var(--dm-hover)] transition-colors"
          title={lang === "en" ? "العربية" : "English"}
        >
          {lang === "en" ? "AR" : "EN"}
        </button>
      </div>

      {/* Footer — profile menu */}
      <div className="px-3 pb-3 pt-1 flex-shrink-0">
        <ProfileMenu user={user} t={t} onLogout={handleLogout} onNav={handleNav} mobile={mobile} />
      </div>
    </div>
  );
}

export default function ProtectedLayout() {
  const { user, loading, maintenanceMode, setMaintenanceMode, authHeader } = useAuth();
  const { lang, toggleLang, t } = useLang();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [disablingMaintenance, setDisablingMaintenance] = useState(false);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  const handleDisableMaintenance = async () => {
    setDisablingMaintenance(true);
    try {
      const current = await axios.get(`${API}/settings`, { headers: authHeader() });
      await axios.put(`${API}/settings`, { ...current.data, maintenance_mode: false }, { headers: authHeader() });
      setMaintenanceMode(false);
      toast.success(t("maintenance_disabled") || "Maintenance mode turned off");
    } catch {
      toast.error(t("maintenance_disable_failed") || "Failed to disable maintenance mode");
    } finally {
      setDisablingMaintenance(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#E4E2DC] dark:bg-[var(--dm-page)]">
        <Loader2 className="w-5 h-5 animate-spin text-[#1B1D21]/20 dark:text-white/20" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Maintenance mode: full-screen block for non-admin (user stays logged in)
  if (maintenanceMode && user?.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#E4E2DC] dark:bg-[var(--dm-page)] p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-[#B3261E]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Settings className="w-8 h-8 text-[#B3261E] animate-spin" style={{ animationDuration: "3s" }} />
          </div>
          <h1 data-testid="maintenance-title" className="text-2xl font-bold text-[#1B1D21] dark:text-[var(--dm-text)] mb-3">
            {t("maintenance_title") || "System Maintenance"}
          </h1>
          <p className="text-sm text-[#6F6C65] dark:text-[var(--dm-muted-text)] leading-relaxed">
            {t("maintenance_message") || "The system is currently under maintenance. Please try again later."}
          </p>
        </div>
      </div>
    );
  }

  const themeColor = user?.theme_color || "#FBCC49";
  // Light text for dark brand colors
  const isDarkBrand = ["#262626", "#6763A9"].includes(themeColor);

  return (
    <div className="h-screen w-screen flex bg-[#F6F5F2] dark:bg-[var(--dm-page)] overflow-hidden">
      {/* Desktop sidebar — sits directly on page bg */}
      <div className="hidden md:flex flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={closeSidebar} />
          <div className={`relative ${lang === "ar" ? "ms-auto" : ""}`}>
            <Sidebar mobile onClose={closeSidebar} />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden" style={{ "--brand": themeColor, "--brand-text": isDarkBrand ? "#FFFFFF" : "#1A1A1A" }}>
        {/* Mobile topbar */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 bg-white dark:bg-[var(--dm-page)] border-b border-[#EFEDE8] dark:border-[var(--dm-divider)]">
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setSidebarOpen(true)}
            className="p-1.5 rounded-lg text-[#6F6C65] dark:text-[var(--dm-muted-text)] hover:bg-[#F6F5F2] dark:hover:bg-[var(--dm-hover)]"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="RFQ Hub" className="h-5 w-auto rounded object-contain" />
            <span className="text-[11px] text-[#6F6C65] dark:text-[var(--dm-muted-text)] font-medium">منصة رفق</span>
          </div>
          <button
            data-testid="mobile-lang-toggle"
            onClick={toggleLang}
            className="text-xs font-medium text-[#6F6C65] dark:text-[var(--dm-muted-text)] hover:text-[#1B1D21] dark:hover:text-[var(--dm-text)] px-2 py-1 rounded-lg hover:bg-[#F6F5F2] dark:hover:bg-[var(--dm-hover)]"
          >
            {lang === "en" ? "AR" : "EN"}
          </button>
        </div>

        {/* Content */}
        <main className="flex-1 overflow-y-auto bg-[#F6F5F2] dark:bg-[var(--dm-page)]">
          {maintenanceMode && (
            <div data-testid="maintenance-banner" className="bg-[#B3261E] px-4 py-2.5 flex items-center justify-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-white animate-spin" style={{ animationDuration: "3s" }} />
                <span className="text-sm font-medium text-white">
                  {user?.role === "admin"
                    ? (t("maintenance_admin_banner") || "Maintenance mode is ON — only you can see the full app")
                    : (t("maintenance_message") || "The system is currently under maintenance. Please try again later.")}
                </span>
              </div>
              {user?.role === "admin" && (
                <button
                  data-testid="disable-maintenance-btn"
                  onClick={handleDisableMaintenance}
                  disabled={disablingMaintenance}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-[#B3261E] text-xs font-semibold hover:bg-white/90 transition-colors disabled:opacity-60"
                >
                  {disablingMaintenance && <Loader2 className="w-3 h-3 animate-spin" />}
                  {t("disable_maintenance") || "Turn Off Maintenance"}
                </button>
              )}
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
